// pages/Usuarios.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  Tree,
  Divider,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  SafetyOutlined,
  KeyOutlined,
  MailOutlined,
  LockOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const Usuarios = () => {
  // Estados
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [menusArbol, setMenusArbol] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentUsuario, setCurrentUsuario] = useState(null);
  const [selectedRol, setSelectedRol] = useState(null);
  const [selectedPermisos, setSelectedPermisos] = useState([]);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('usuarios');

  // Configuración de columnas
  const columns = [
    {
      title: 'Email',
      dataIndex: 'usuario',
      key: 'usuario',
      width: 200,
      sorter: (a, b) => a.usuario?.localeCompare(b.usuario || '') || 0,
      render: (usuario) => (
        <Space>
          <MailOutlined style={{ color: '#1890ff' }} />
          <Text strong>{usuario}</Text>
        </Space>
      ),
      fixed: 'left',
    },
    {
      title: 'Usuario Corto',
      dataIndex: 'UsuarioCorto',
      key: 'UsuarioCorto',
      width: 150,
      sorter: (a, b) => a.UsuarioCorto?.localeCompare(b.UsuarioCorto || '') || 0,
      render: (usuarioCorto) => (
        <Tag color="blue">{usuarioCorto}</Tag>
      ),
    },
    {
      title: 'Nombre Completo',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 200,
      sorter: (a, b) => a.nombre?.localeCompare(b.nombre || '') || 0,
    },
    {
      title: 'Rol',
      dataIndex: 'rol_nombre',
      key: 'rol_nombre',
      width: 150,
      render: (rol) => (
        <Tag color={rol === 'super_admin' ? 'red' : rol === 'admin' ? 'orange' : 'green'}>
          {rol || 'Sin rol'}
        </Tag>
      ),
      filters: [
        { text: 'Super Admin', value: 'super_admin' },
        { text: 'Admin', value: 'admin' },
        { text: 'Usuario', value: 'usuario' },
        { text: 'Sin rol', value: null },
      ],
      onFilter: (value, record) => record.rol_nombre === value,
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 100,
      render: (activo) => (
        activo  ? (
          <Badge status="success" text="Activo" />
        ) : (
          <Badge status="error" text="Inactivo" />
        )
      ),
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 },
      ],
      onFilter: (value, record) => record.activo === value,
    },
    {
      title: 'Fecha Creación',
      dataIndex: 'fecha_creacion',
      key: 'fecha_creacion',
      width: 150,
      render: (fecha) => fecha ? new Date(fecha).toLocaleDateString() : '-',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Desactivar usuario?"
            description="Esta acción desactivará el usuario"
            onConfirm={() => handleDelete(record.usuario)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Desactivar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchUsuarios();
    fetchRoles();
    fetchMenusArbol();
  }, []);

  const fetchUsuarios = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        search: searchText,
        sortField: params.sortField || 'usuario',
        sortOrder: params.sortOrder || 'ASC'
      };
      
      const response = await axios.get(`${BASE_URL}/api/usuarios`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      setUsuarios(response.data.data || []);
      console.log(response.data.data);
      
      
    } catch (error) {
      console.error('❌ Error cargando usuarios:', error);
      message.error('Error al cargar los usuarios ');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/usuarios/roles/listar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando roles:', error);
    }
  };

  const fetchMenusArbol = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/usuarios/menus/arbol`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenusArbol(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando menús:', error);
    }
  };

  const fetchPermisosRol = async (rolId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/usuarios/permisos/${rolId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const permisos = response.data.data || [];
      setSelectedPermisos(permisos.map(p => p.id));
    } catch (error) {
      console.error('❌ Error cargando permisos:', error);
    }
  };

  const handleRolChange = (rolId) => {
    setSelectedRol(rolId);
    if (rolId) {
      fetchPermisosRol(rolId);
    } else {
      setSelectedPermisos([]);
    }
  };

  const handleGuardarPermisos = async () => {
    if (!selectedRol) {
      message.warning('Seleccione un rol primero');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BASE_URL}/api/usuarios/permisos/${selectedRol}`,
        { permisos: selectedPermisos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Permisos actualizados correctamente');
    } catch (error) {
      console.error('❌ Error guardando permisos:', error);
      message.error('Error al guardar los permisos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalType('create');
    setCurrentUsuario(null);
    form.resetFields();
    form.setFieldsValue({
      activo: true,
      esmaster: false
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentUsuario(record);
    form.setFieldsValue({
      usuario: record.usuario,
      UsuarioCorto: record.UsuarioCorto,
      nombre: record.nombre,
      esmaster: record.esmaster,
      activo: record.activo,
      rol_id: record.rol_id
    });
    setModalVisible(true);
  };

  const handleDelete = async (usuario) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/usuarios/${usuario}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsuarios(usuarios.filter(u => u.usuario !== usuario));
      message.success('Usuario desactivado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando usuario:', error);
      message.error(error.response?.data?.error || 'Error al desactivar el usuario');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/usuarios`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Usuario creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/usuarios/${currentUsuario.usuario}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Usuario actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchUsuarios();
      
    } catch (error) {
      console.error('❌ Error guardando usuario:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    fetchUsuarios();
  };

  const handleResetSearch = () => {
    setSearchText('');
    setSearchLoading(false);
    fetchUsuarios();
  };

  // Convertir árbol de menús para Tree component
  const convertToTreeData = (menus) => {
    return menus.map(menu => ({
      key: menu.id,
      title: menu.menu_label,
      children: menu.children ? convertToTreeData(menu.children) : undefined
    }));
  };

  const onCheckPermisos = (checkedKeys) => {
    setSelectedPermisos(checkedKeys);
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por email, usuario corto o nombre..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
      </Space>
    </div>
  );

  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Usuarios" 
          value={usuarios.length}
          prefix={<UserOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activos" 
          value={usuarios.filter(u => u.activo).length}
          suffix={`/ ${usuarios.length}`}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Roles" 
          value={roles.length}
          prefix={<TeamOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Super Administradores" 
          value={usuarios.filter(u => u.rol_nombre === 'super_admin').length}
          prefix={<SafetyOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Usuarios"
        subtitle={`${usuarios.length} usuarios registrados`}
        icon={<UserOutlined />}
        loading={loading}
        onRefresh={() => fetchUsuarios()}
        onCreate={handleCreate}
        badgeCount={usuarios.length}
        badgeStatus="processing"
        extra={
          <Button
            icon={<KeyOutlined />}
            onClick={() => setActiveTab('permisos')}
          >
            Configurar Permisos
          </Button>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Usuarios" key="usuarios">
            {estadisticas}
            {searchBar}
            
            <Table
              columns={columns}
              dataSource={usuarios}
              rowKey={(record) => record.usuario}
              loading={loading}
              scroll={{ x: 1200 }}
              style={{
                borderRadius: '8px',
                overflow: 'hidden'
              }}
              size="middle"
              locale={{
                emptyText: 'No se encontraron usuarios'
              }}
            />
          </TabPane>
          
          <TabPane tab="Permisos por Rol" key="permisos">
            <Card>
              <Row gutter={24}>
                <Col span={8}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Seleccionar Rol:</Text>
                  </div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Seleccione un rol"
                    onChange={handleRolChange}
                    value={selectedRol}
                    allowClear
                  >
                    {roles.map(rol => (
                      <Option key={rol.id} value={rol.id}>
                        <Space>
                          {rol.nombre === 'super_admin' && <SafetyOutlined />}
                          {rol.nombre === 'admin' && <TeamOutlined />}
                          {rol.nombre === 'usuario' && <UserOutlined />}
                          <span>{rol.nombre}</span>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            - {rol.descripcion}
                          </Text>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={16}>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong>Permisos del Rol:</Text>
                    <Button 
                      type="primary" 
                      onClick={handleGuardarPermisos}
                      disabled={!selectedRol}
                    >
                      Guardar Permisos
                    </Button>
                  </div>
                  {selectedRol ? (
                    <div style={{ 
                      border: '1px solid #f0f0f0', 
                      borderRadius: '8px', 
                      padding: '16px',
                      maxHeight: '500px',
                      overflow: 'auto'
                    }}>
                      <Tree
                        checkable
                        checkStrictly
                        defaultExpandAll
                        checkedKeys={selectedPermisos}
                        onCheck={onCheckPermisos}
                        treeData={convertToTreeData(menusArbol)}
                      />
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px', 
                      backgroundColor: '#fafafa',
                      borderRadius: '8px'
                    }}>
                      <Text type="secondary">Seleccione un rol para ver y configurar sus permisos</Text>
                    </div>
                  )}
                </Col>
              </Row>
              
              <Divider />
              
              <Card size="small" style={{ backgroundColor: '#e6f7ff' }}>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  <strong>💡 Información sobre permisos:</strong>
                  <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>Super Admin:</strong> Tiene acceso a todas las funcionalidades del sistema</li>
                    <li><strong>Admin:</strong> Puede acceder a gestión de usuarios y la mayoría de funcionalidades</li>
                    <li><strong>Usuario:</strong> Acceso básico a funcionalidades operativas</li>
                    <li>Los permisos se aplican en tiempo real al iniciar sesión</li>
                    <li>Marque/desmarque los checkboxes para asignar o quitar permisos</li>
                  </ul>
                </div>
              </Card>
            </Card>
          </TabPane>
        </Tabs>
      </PageContainer>

      {/* Modal para crear/editar usuario */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Usuario' : 'Editar Usuario'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="usuario"
                label="Email"
                rules={[
                  { required: true, message: 'Por favor ingrese el email' },
                  { type: 'email', message: 'Ingrese un email válido' }
                ]}
                tooltip="Email que usará para iniciar sesión"
              >
                <Input 
                  placeholder="ejemplo@hotel.com"
                  prefix={<MailOutlined />}
                  disabled={modalType === 'edit'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="UsuarioCorto"
                label="Usuario Corto"
                rules={[{ required: true, message: 'Por favor ingrese el usuario corto' }]}
                tooltip="Nombre corto para identificación rápida"
              >
                <Input 
                  placeholder="Ej: jperez"
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nombre"
                label="Nombre Completo"
                rules={[{ required: true, message: 'Por favor ingrese el nombre completo' }]}
              >
                <Input 
                  placeholder="Nombre completo del usuario"
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="password"
                  label="Contraseña"
                  rules={[
                    { required: true, message: 'Por favor ingrese la contraseña' },
                    { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
                  ]}
                >
                  <Input.Password 
                    placeholder="********"
                    prefix={<LockOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {modalType === 'edit' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="password"
                  label="Nueva Contraseña"
                  tooltip="Dejar en blanco para mantener la contraseña actual"
                  rules={[
                    { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
                  ]}
                >
                  <Input.Password 
                    placeholder="******** (dejar en blanco para no cambiar)"
                    prefix={<LockOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="rol_id"
                label="Rol"
                tooltip="Rol que determina los permisos del usuario"
              >
                <Select placeholder="Seleccione un rol" allowClear>
                  {roles.map(rol => (
                    <Option key={rol.id} value={rol.id}>
                      <Space>
                        {rol.nombre === 'super_admin' && <SafetyOutlined />}
                        {rol.nombre === 'admin' && <TeamOutlined />}
                        {rol.nombre === 'usuario' && <UserOutlined />}
                        <span>{rol.nombre}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="esmaster"
                label="Super Administrador"
                valuePropName="checked"
                tooltip="Los super administradores tienen acceso total al sistema"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="activo"
                label="Estado"
                valuePropName="checked"
                tooltip="Usuario activo puede iniciar sesión"
              >
                <Switch
                  checkedChildren="Activo"
                  unCheckedChildren="Inactivo"
                />
              </Form.Item>
            </Col>
          </Row>

          <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
            <div style={{ fontSize: '13px', color: '#666' }}>
              <strong>📝 Información importante:</strong>
              <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                <li>El email será el usuario para iniciar sesión</li>
                <li>Los roles determinan los permisos de acceso al sistema</li>
                <li>Los super administradores tienen acceso a todas las funcionalidades</li>
                <li>Puede configurar permisos específicos en la pestaña "Permisos por Rol"</li>
              </ul>
            </div>
          </Card>
        </Form>
      </Modal>
    </>
  );
};

export default Usuarios;